// 용사의 시련 탭의 내용 모델(3개월 C7, 순수 로직·DOM 없음). 데이터·판정은 shared/content/trials.ts, 상태는 view.trials(done·chests·open)다.
// 시련판마다 머리 줄(이름·n/10·상자 상태) 다음에 시련 줄 10개: 잠김(그 스테이지를 아직 안 깸) / 도전 가능 / 이룸 ✓.
import { stageLabel } from '../shared/content/stages';
import { jobGateDef } from '../shared/content/jobQuest';
import { TRIALS, TRIAL_BOARDS, boardTrialsOf, secText, trialDef, type TrialBoardDef, type TrialDef, type TrialReward } from '../shared/content/trials';
import type { QuestRow } from './questWindowModel';

/** view.trials에서 읽는 값(TrialsView의 부분) */
export interface TrialStateLike {
  readonly done: readonly string[];
  readonly chests: readonly string[];
  readonly open: readonly string[];
}

/** 시련 보상 문구: 「골드 500 · 강화석 2개」 */
export function trialRewardText(r: TrialReward): string {
  return '골드 ' + r.gold.toLocaleString('ko-KR') + ' · 강화석 ' + r.stones + '개';
}

/** 시련 한 줄(시련판 목록·「다음 시련」 줄 공용). tag를 주면 표지를 바꾼다 */
export function trialRow(t: TrialDef, s: TrialStateLike, tag?: string): QuestRow {
  const done = s.done.includes(t.id), open = s.open.includes(t.id);
  const lines = [t.hint];
  if (done) lines.push('이미 이룬 시련이에요. 다시 도전해도 보상은 한 번만 받아요.');
  const locked = !done && !open;
  return {
    id: 'trial:' + t.id, kind: 'trial', title: t.name,
    tag: tag ?? (done ? '이룸 ✓' : open ? '도전 가능' : '잠김'),
    tone: done ? 'done' : open ? 'gold' : 'lock',
    sub: locked ? stageLabel(t.stage) + '을 먼저 깨세요' : trialRewardText(t.reward),
    lines, rewards: done ? [] : ['처음 이루면 ' + trialRewardText(t.reward)],
    action: locked ? null : { label: '시련 도전', run: { kind: 'trial', id: t.id } },
    blocked: locked ? stageLabel(t.stage) + ' 스테이지를 먼저 깨야 열려요' : '',
  };
}

/** 시련판 머리 줄: 이름·n/10·상자 상태. 10개를 모두 이뤘고 상자가 남았을 때만 「시련 상자 열기」 */
export function boardRow(b: TrialBoardDef, s: TrialStateLike): QuestRow {
  const n = boardTrialsOf(b.id).filter((t) => s.done.includes(t.id)).length;
  const got = s.chests.includes(b.id), ready = n >= 10 && !got;
  return {
    id: 'trialboard:' + b.id, kind: 'board', title: b.name, tag: n + '/10', tone: got ? 'done' : ready ? 'gold' : b.hard ? 'hard' : 'easy',
    sub: got ? '시련 상자 받음 ✓' : ready ? '시련 상자를 열 수 있어요' : '10개를 모두 이루면 시련 상자',
    lines: ['아래 시련 10개를 모두 이루면 시련 상자를 한 번 열 수 있어요.', '이룬 시련 ' + n + '/10'],
    rewards: ['시련 상자 ' + trialRewardText(b.chest)],
    action: ready ? { label: '시련 상자 열기', run: { kind: 'trialChest', board: b.id } } : null,
    blocked: ready ? '' : got ? '시련 상자를 이미 받았어요' : '시련 ' + (10 - n) + '개를 더 이루면 열 수 있어요',
  };
}

/** 헬 시련판이 보이는가: 그 판 시련 중 하나라도 열렸거나 이뤘을 때(그 전에는 잠긴 안내 한 줄이 판 6개를 대신한다) */
export function boardVisible(b: TrialBoardDef, s: TrialStateLike): boolean {
  if (b.diff !== 'hell') return true;
  return boardTrialsOf(b.id).some((t) => s.open.includes(t.id) || s.done.includes(t.id));
}

/** 숨은 헬 시련판을 대신하는 잠긴 안내 줄 */
export function hellLockedRow(n = 6): QuestRow {
  return {
    id: 'trialboard:hell-locked', kind: 'board', title: '헬 시련판 ' + n + '개', tag: '잠김', tone: 'lock',
    sub: '헬 지역 스테이지를 처음 깨면 열려요', lines: ['헬 시련판 ' + n + '개 · 헬 지역 스테이지를 처음 깨면 열려요'],
    rewards: [], action: null, blocked: '헬 지역 스테이지를 처음 깨면 열려요',
  };
}

/** 「시련」 탭 줄들: 시련판마다 머리 줄 + 시련 10개. 아직 안 열린 헬 시련판은 안내 줄 하나로 접는다 */
export function buildTrialRows(s: TrialStateLike): QuestRow[] {
  const rows: QuestRow[] = [];
  let hellHidden = 0;
  for (const b of TRIAL_BOARDS) {
    if (!boardVisible(b, s)) { hellHidden++; continue; }
    rows.push(boardRow(b, s));
    for (const t of boardTrialsOf(b.id)) rows.push(trialRow(t, s));
  }
  if (hellHidden > 0) rows.push(hellLockedRow(hellHidden));
  return rows;
}

/** 열린 것 중 아직 안 이룬 첫 시련(없으면 null) */
export function nextTrial(s: TrialStateLike): TrialDef | null {
  return TRIALS.find((t) => s.open.includes(t.id) && !s.done.includes(t.id)) ?? null;
}

/** 진행 중 탭 이정표 아래 「다음 시련」 한 줄(없으면 null) */
export function nextTrialRow(s: TrialStateLike): QuestRow | null {
  const t = nextTrial(s);
  if (!t) return null;
  const r = trialRow(t, s, '다음 시련');
  return { ...r, id: 'trialnext', sub: t.hint.split(' · ')[0] + ' · ' + trialRewardText(t.reward), rewards: [] };
}

/** 시련 상자 연타 잠금용: 이 시련판 상자를 지금 열 수 있나 */
export function chestReady(b: TrialBoardDef['id'], s: TrialStateLike): boolean {
  return !s.chests.includes(b) && boardTrialsOf(b).every((t) => s.done.includes(t.id));
}

/** 결과 화면이 읽는 값(ResultView의 시련 부분 + 판 기록) */
export interface TrialResultLike {
  readonly trialId?: string;
  readonly trialMet?: boolean;
  readonly trialNew?: boolean;
  readonly trialLow?: number;
  readonly trialStones?: number;
  readonly trialChest?: string;
  /** 전직 관문(C10-5): 이번 판으로 그 단계 관문 3개를 모두 넘었으면 단계(1 갈래 선택·2 승급) */
  readonly jobReady?: number;
  readonly cleared: boolean;
  readonly gold: number;
  readonly clearTicks: number;
  readonly maxCombo: number;
  readonly stars: number;
}

/** 전직 관문 판 결과 문구(C10-5): chip = 관문 이름(「전직 I · 연격의 관문」), 관문을 모두 넘으면 chest 줄에 교관 안내 */
function jobResultLines(r: TrialResultLike): { chip: string; title: string; line: string; chest: string; defeat: string } | null {
  const def = jobGateDef(r.trialId);
  if (!def) return null;
  const chip = def.name, defeat = '전직 관문은 경험치가 없어요 · 관문을 넘으면 골드와 강화석을 받아요';
  if (!r.cleared) return { chip, title: '관문 실패', line: '', chest: '', defeat };
  if (r.trialMet) {
    const guide = r.jobReady === 2 ? '관문 모두 통과! 교관 세르에게 가서 전직 II 승급을 받으세요'
      : r.jobReady ? '관문 모두 통과! 교관 세르에게 가서 갈래를 고르세요' : '';
    if (r.trialNew) {
      const stones = r.trialStones ?? def.reward.stones;
      return { chip, title: '관문 통과', line: '관문 통과! · 골드 ' + r.gold.toLocaleString('ko-KR') + ' · 강화석 ' + stones + '개', chest: guide, defeat };
    }
    return { chip, title: '관문 통과', line: '이미 넘은 관문 · 기록만 남아요', chest: guide, defeat };
  }
  const miss = def.kind === 'guard' ? '인내 · 최저 HP ' + (r.trialLow ?? 0) + '% / 목표 ' + def.target + '%'
    : def.kind === 'combo' ? '연격 · 최대 콤보 ' + r.maxCombo + ' / 목표 ' + def.target
    : '완벽한 승리 · 별 ' + r.stars + ' / ' + def.target;
  return { chip, title: '관문 미달', line: '조건 미달 · ' + miss, chest: '', defeat };
}

/** 결과 화면 문구: chip 「시련 · 이름」, title, line(보상·이미 이룸·모자란 점), chest(시련판 완성 안내·전직 교관 안내, 없으면 ''), defeat(패배 화면 안내). 시련·관문 판이 아니면 null */
export function trialResultLines(r: TrialResultLike): { chip: string; title: string; line: string; chest: string; defeat: string } | null {
  const gate = jobResultLines(r);
  if (gate) return gate;
  const def = trialDef(r.trialId);
  if (!def) return null;
  const defeat = '시련 판은 경험치가 없어요 · 보상은 시련을 이룰 때만 받아요';
  const chip = '시련 · ' + def.name;
  if (!r.cleared) return { chip, title: '시련 실패', line: '', chest: '', defeat };
  if (r.trialMet) {
    if (r.trialNew) {
      const stones = r.trialStones ?? def.reward.stones;
      return { chip, title: '시련 클리어', line: '시련 달성! · 골드 ' + r.gold.toLocaleString('ko-KR') + ' · 강화석 ' + stones + '개', chest: r.trialChest ? '시련판 완성! 퀘스트 창 시련 탭에서 상자를 여세요' : '', defeat };
    }
    return { chip, title: '시련 클리어', line: '이미 이룬 시련', chest: '', defeat };
  }
  const sec = Math.floor(r.clearTicks / 60);
  const miss = def.kind === 'speed' ? '속공 · 걸린 시간 ' + secText(sec) + ' / 목표 ' + secText(def.target)
    : def.kind === 'guard' ? '무혈 · 최저 HP ' + (r.trialLow ?? 0) + '% / 목표 ' + def.target + '%'
    : def.kind === 'combo' ? '연격 · 최대 콤보 ' + r.maxCombo + ' / 목표 ' + def.target
    : '완벽한 승리 · 별 ' + r.stars + ' / ' + def.target;
  return { chip, title: '시련 미달', line: '조건 미달 · ' + miss, chest: '', defeat };
}
